import React, { useCallback, useEffect, useRef, useState } from 'react';
import { userAPI } from '../../services/api';
import { instructorFileAPI } from '../../services/instructorFileAPI';
import { Button } from '../ui/button';

const InstructorFiles = ({ user }) => {
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [instructors, setInstructors] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const instructorId = isAdmin ? selectedId : user?.id;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInstructors, setLoadingInstructors] = useState(isAdmin);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const requestVersion = useRef(0);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    userAPI.getInstructors().then(response => {
      if (active) setInstructors(response.data || []);
    }).catch(() => {
      if (active) setError('Багшийн жагсаалтыг ачаалж чадсангүй. Хуудсыг дахин нээнэ үү.');
    }).finally(() => {
      if (active) setLoadingInstructors(false);
    });
    return () => { active = false; };
  }, [isAdmin]);

  const loadFiles = useCallback(async () => {
    const version = ++requestVersion.current;
    setFiles([]);
    setError('');
    if (!instructorId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await instructorFileAPI.list(instructorId);
      if (version === requestVersion.current) setFiles((response.objects || []).filter(file => !file.isFolder));
    } catch (err) {
      if (version === requestVersion.current) setError(`Материалыг ачаалж чадсангүй: ${err.message}`);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [instructorId]);

  useEffect(() => {
    setNotice('');
    loadFiles();
    return () => { requestVersion.current += 1; };
  }, [loadFiles]);

  const upload = async (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length || !instructorId) return;
    setBusy(true);
    setError('');
    setNotice('');
    let completed = 0;
    let uploadError = '';
    try {
      for (const file of selected) {
        await instructorFileAPI.upload(instructorId, file);
        completed += 1;
      }
    } catch (err) {
      uploadError = `Файл байршуулахад алдаа гарлаа: ${err.message}`;
    }
    await loadFiles();
    setNotice(`${selected.length} файлаас ${completed} файл байршууллаа.`);
    if (uploadError) setError(uploadError);
    if (inputRef.current) inputRef.current.value = '';
    setBusy(false);
  };

  const download = async (file) => {
    setBusy(true);
    setError('');
    try {
      await instructorFileAPI.download(instructorId, file);
    } catch (err) {
      setError(`Файлыг татаж чадсангүй: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (file) => {
    if (!window.confirm(`“${file.key}” файлыг устгах уу?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await instructorFileAPI.delete(instructorId, file.fullKey);
      await loadFiles();
    } catch (err) {
      setError(`Файлыг устгаж чадсангүй: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 mb-6">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">{isAdmin ? 'Багшид зориулсан материал' : 'Миний материал'}</h1>
      <p className="text-sm text-gray-600 mb-5">
        {isAdmin ? 'Багшаа сонгож хичээлийн материал байршуулна. Тухайн багш болон админ энэ хэсэгт хандана.' : 'Админаас танд зориулж байршуулсан хичээлийн материалыг эндээс татна уу.'}
      </p>
      {isAdmin && (
        <div className="mb-5">
          <label htmlFor="material-instructor" className="block text-sm font-medium mb-2">Багш</label>
          <select id="material-instructor" value={selectedId} onChange={e => setSelectedId(e.target.value)} disabled={busy || loadingInstructors} className="w-full max-w-lg border border-gray-300 rounded-md p-2">
            <option value="">{loadingInstructors ? 'Багшийн жагсаалтыг ачаалж байна...' : 'Багш сонгоно уу'}</option>
            {instructors.map(instructor => (
              <option key={instructor.id} value={instructor.id}>
                {instructor.full_name || [instructor.first_name, instructor.last_name].filter(Boolean).join(' ') || instructor.username} ({instructor.username || instructor.email || instructor.id})
              </option>
            ))}
          </select>
          {!loadingInstructors && !instructors.length && !error && <p className="text-sm text-gray-500 mt-2">Бүртгэлтэй багш байхгүй байна.</p>}
        </div>
      )}
      {error && <p role="alert" className="bg-red-50 text-red-700 rounded-md p-3 mb-4">{error}</p>}
      {notice && <p role="status" className="bg-blue-50 text-blue-700 rounded-md p-3 mb-4">{notice}</p>}
      {instructorId && (
        <>
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <Button onClick={loadFiles} variant="outline" disabled={busy || loading}>Шинэчлэх</Button>
            {isAdmin && <>
              <input ref={inputRef} type="file" multiple onChange={upload} disabled={busy || loading} className="hidden" aria-label="Материалын файл сонгох" />
              <Button onClick={() => inputRef.current?.click()} disabled={busy || loading}>Файл байршуулах</Button>
            </>}
            {busy && <span role="status" className="text-sm text-gray-600">Боловсруулж байна...</span>}
          </div>
          {loading ? <p role="status">Материалыг ачаалж байна...</p> : files.length ? (
            <ul className="divide-y divide-gray-200">
              {files.map(file => (
                <li key={file.fullKey} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium break-words">{file.key}</p>
                    <p className="text-sm text-gray-500">{((file.size || 0) / 1024).toFixed(1)} KB{file.lastModified ? ` · ${new Date(file.lastModified).toLocaleDateString()}` : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => download(file)} disabled={busy}>Татах</Button>
                    {isAdmin && <Button variant="outline" onClick={() => remove(file)} disabled={busy}>Устгах</Button>}
                  </div>
                </li>
              ))}
            </ul>
          ) : !error && <p className="text-gray-500 py-4">Одоогоор материал байршуулаагүй байна.</p>}
        </>
      )}
    </section>
  );
};

export default InstructorFiles;
